import { BaseModel } from '../../models'
import { Utils } from '../../../helpers/'

import * as DataModels from '../../models'

export class ModelFactory {

  private AVAILABLE_CLASSES: {[className: string]: any}

  public constructor(
    _modelClasses: any[],
    private _utils: Utils
  ) {
    const libClasses = Object.keys(DataModels).map((key) => DataModels[key]).filter((modelClass) => modelClass.name)
    this.AVAILABLE_CLASSES = libClasses.concat(_modelClasses)
      .reduce((classObject, klass) => {
        classObject[klass.name.toLowerCase()] = klass
        return classObject
      }, {})
  }

  public instantiateClass<T extends BaseModel>(className: string, ...constructorArguments: any[]): T {
    const normalizedClassName = this.normalizedClassName(className)

    if (typeof normalizedClassName === 'undefined') {
      throw new Error(`Cannot instantiate class '${className}'. This class is not supported.`)
    }

    const instanceOfClass = Object.create(this.AVAILABLE_CLASSES[normalizedClassName.toLowerCase()].prototype)
    const constructorArgNames = instanceOfClass.constructor.toString()
      .match(/function[^(]*\(([^)]*)\)/)[1].split(',')
      .reduce((args, argName) => {
        const a = argName.trim()
        if (a.length > 0) { args.push(a) }
        return args
       }, [])

    while (constructorArgNames.length > constructorArguments.length) {
      constructorArguments.push({})
    }

    instanceOfClass.constructor.apply(instanceOfClass, constructorArguments)
    return instanceOfClass
  }

  public createAndInitialize<T extends BaseModel>(
    className: string,
    initialValues: {[propertyName: string]: any}
  ): T {
    const instance = this.instantiateClass<T>(className)
    Object.keys(initialValues).forEach((propertyName) => {
      this.setPropertyOnModel(instance, propertyName, initialValues[propertyName])
    })
    return instance
  }

  public normalizedClassName(name: string): string {
    name = name.toLowerCase()
    if (this.AVAILABLE_CLASSES[name]) {
      return this.AVAILABLE_CLASSES[name].name
    }

    let cleanedName = name.toLowerCase().replace(/[^A-Za-z0-9]/g, '')
    if (this.AVAILABLE_CLASSES[cleanedName]) {
      return this.AVAILABLE_CLASSES[cleanedName].name
    }

    if (this._utils.isPlural(cleanedName)) {
      cleanedName = this._utils.singularize(cleanedName)
      if (this.AVAILABLE_CLASSES[cleanedName]) {
        return this.AVAILABLE_CLASSES[cleanedName].name
      }
    }

    return undefined
  }

  public setPropertyOnModel(modelInstance: BaseModel, propertyName: string, propertyValue: any): void {
    const exceptionPrefix = `Cannot set property '${propertyName}':`
    if (typeof propertyValue === 'function') {
      throw new Error(`${exceptionPrefix} Functions cannot be defined using this method for security reasons.`)
    } else if (propertyName.startsWith('_')) {
      throw new Error(`${exceptionPrefix} It is meant to be a private attribute and thus cannot be set externally.`)
    }

    if (propertyName === 'id' && typeof propertyValue === 'number') { propertyValue = propertyValue.toString() }

    const setterFunctionName = `set${this._utils.capitalizeFirst(propertyName)}`
    if (typeof modelInstance[setterFunctionName] === 'function') {
      modelInstance[setterFunctionName].call(modelInstance, propertyValue)
    // do we also want/need to check if `propertyName instanceof modelInstance`?
    // if so, we'd need to explicitly set these values on the models in the constructor
    } else if (typeof modelInstance[propertyName] !== 'function') {
      modelInstance[propertyName] = propertyValue
    } else {
      const className = typeof modelInstance['getClass'] === 'function'
        ? modelInstance.getClass().name : 'unknown'
      throw new Error(`${exceptionPrefix} It is not present in model of type '${className}'`)
    }
  }

  public getNullObject<T extends BaseModel>(className: string, args: {} = {}): T {
    return this.instantiateClass<T>(className, args)
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/data/factories/model-factory/model-factory.ts